<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive } from "vue"
import AppShell from "../Layout/AppShell.vue"
import { UiDataTable } from "@/components"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"
import { formatDate } from "@/utils/date"

type StockMovementRow = {
  id: number
  company: string
  product_id: number
  product: string
  movement_type: string
  raw_movement_type: string
  reason: string
  quantity: string
  signed_quantity: string
  unit_cost: string
  reference: string
  notes: string
  created_by: string
  created_at: string
}

type SortState = {
  key: string
  direction: "asc" | "desc"
}

const props = defineProps<{
  product: {
    id: number
    company: string
    name: string
    unit: string
  }
  stock_movements: StockMovementRow[]
  movement_types: Array<{ label: string; value: string }>
  query: {
    q?: string
    company_id?: string
    product_id: number
    movement_type?: string
    sort_by?: string
    sort_direction?: "asc" | "desc"
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
}>()

const columns: UiDataTableColumn[] = [
  { key: "created_at", label: "Data", align: "center", sortable: true },
  { key: "movement_type", label: "Tipo", align: "center", sortable: true },
  { key: "reason", label: "Motivo" },
  { key: "quantity", label: "Quantidade", align: "right", sortable: true },
  { key: "signed_quantity", label: "Impacto", align: "right" },
  { key: "unit_cost", label: "Custo", align: "right" },
  { key: "reference", label: "Referencia" },
  { key: "created_by", label: "Criado por" },
  { key: "notes", label: "Observacoes" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "movement_type",
    label: "Tipo",
    options: props.movement_types,
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  movement_type: props.query.movement_type ?? "",
})

const currentSort = reactive<SortState>({
  key: props.query.sort_by ?? "created_at",
  direction: props.query.sort_direction ?? "desc",
})

const movementTypeBadgeClassByType: Record<string, string> = {
  in: "border-emerald-200 bg-emerald-50 text-emerald-700",
  out: "border-rose-200 bg-rose-50 text-rose-700",
  adjust: "border-amber-200 bg-amber-50 text-amber-700",
}

function movementTypeBadgeClass(rawMovementType: string) {
  return movementTypeBadgeClassByType[rawMovementType] ?? "border-slate-200 bg-slate-100 text-slate-600"
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    product_id: props.product.id,
    company_id: String(props.query.company_id ?? ""),
    q: String(overrides.q ?? props.query.q ?? ""),
    movement_type: String(overrides.movement_type ?? selectedFilters.movement_type ?? ""),
    sort_by: String(overrides.sort_by ?? currentSort.key ?? "created_at"),
    sort_direction: String(overrides.sort_direction ?? currentSort.direction ?? "desc"),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/stock_movements/product_movements", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

function updateSort(sort: SortState) {
  currentSort.key = sort.key
  currentSort.direction = sort.direction
  fetchWithQuery({ sort_by: sort.key, sort_direction: sort.direction, page: 1 })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold text-slate-900">Movimentacoes do item</h1>
            <p class="mt-2 text-sm text-slate-600">{{ product.name }} - {{ product.company }} - Unidade: {{ product.unit }}</p>
          </div>
          <Link
            href="/stock_movements"
            class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          >
            Voltar para estoque
          </Link>
        </div>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="stock_movements"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :sort="currentSort"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @update:sort="updateSort"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #cell="{ row, column, value }">
          <span v-if="column.key === 'created_at'">{{ formatDate((row as StockMovementRow).created_at) }}</span>
          <span
            v-else-if="column.key === 'movement_type'"
            class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold"
            :class="movementTypeBadgeClass((row as StockMovementRow).raw_movement_type)"
          >
            {{ (row as StockMovementRow).movement_type }}
          </span>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>
    </div>
  </AppShell>
</template>

